import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { formatApiErrorDetail } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Play, Clock, Send, PanelsTopLeft } from 'lucide-react';

export default function TranscriptionWorkspace() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [timerRunning, setTimerRunning] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetchDoc();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchDoc = async () => {
    try {
      const { data } = await api.get(`/documents/${id}`);
      setDoc(data);
      if (data.ocr_text) setText(data.ocr_text);
      if (data.transcribed_text) setText(data.transcribed_text);
      if (data.storage_path) loadImage(data.storage_path);
      if (data.status === 'typing' && data.typing_started_at) {
        const started = new Date(data.typing_started_at);
        const elapsed = Math.floor((Date.now() - started.getTime()) / 1000);
        setElapsedSeconds(elapsed);
        startTimer();
      }
    } catch {
      toast.error('Document not found');
      navigate('/tasks');
    } finally {
      setLoading(false);
    }
  };

  const loadImage = async (path) => {
    try {
      const response = await api.get(`/files/${path}`, { responseType: 'blob' });
      setImageUrl(URL.createObjectURL(response.data));
    } catch { /* silent */ }
  };

  const startTimer = useCallback(() => {
    if (timerRef.current) return;
    setTimerRunning(true);
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
    }, 1000);
  }, []);

  const handleStartTyping = async () => {
    try {
      await api.post(`/documents/${id}/start-typing`);
      setDoc(prev => ({ ...prev, status: 'typing' }));
      startTimer();
      toast.success('Timer started! Begin transcribing.');
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const handleSubmit = async () => {
    if (!text.trim()) { toast.error('Please enter the transcription'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/documents/${id}/submit`, { transcribed_text: text });
      if (timerRef.current) clearInterval(timerRef.current);
      setTimerRunning(false);
      toast.success(data.message);
      navigate(`/document/${id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h > 0 ? h + ':' : ''}${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-[var(--brand-primary)]" />
      </div>
    );
  }
  if (!doc) return null;

  const canStart = doc.status === 'assigned';
  const canType = doc.status === 'typing' || doc.status === 'assigned';

  return (
    <div className="h-screen flex flex-col" data-testid="transcription-workspace">
      {/* Top bar */}
      <div className="h-14 bg-white border-b border-[var(--brand-border)] flex items-center justify-between px-4 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            data-testid="workspace-back"
            onClick={() => navigate('/tasks')}
            className="text-[var(--brand-text-sec)] hover:text-[var(--brand-text)]"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-sm font-semibold text-[var(--brand-text)] line-clamp-1">{doc.title}</p>
            <p className="text-xs text-[var(--brand-text-sec)]">
              {doc.difficulty} difficulty | ~{doc.estimated_minutes} min est.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          {/* Timer */}
          <div
            className={`flex items-center gap-1.5 px-3 py-1.5 font-mono text-sm font-bold ${
              timerRunning ? 'bg-[var(--brand-danger)]/10 text-[var(--brand-danger)]' : 'bg-[var(--brand-border)] text-[var(--brand-text-sec)]'
            }`}
            data-testid="workspace-timer"
          >
            <Clock size={14} />
            {formatTime(elapsedSeconds)}
          </div>
          {/* Pay */}
          <span className="text-lg font-black font-mono text-[var(--brand-primary)]" data-testid="workspace-pay">
            ${(doc.selected_price || doc.offered_price || 0).toFixed(2)}
          </span>
        </div>
      </div>

      {/* Main split area */}
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Left: Document Image */}
        <div className="lg:w-1/2 h-1/2 lg:h-full border-b lg:border-b-0 lg:border-r border-[var(--brand-border)] overflow-auto bg-[var(--brand-bg)]">
          {imageUrl ? (
            <img
              src={imageUrl}
              alt="Document"
              className="w-full h-full object-contain p-4"
              data-testid="workspace-image"
            />
          ) : (
            <div className="flex items-center justify-center h-full text-[var(--brand-text-sec)]">
              Loading document...
            </div>
          )}
        </div>

        {/* Right: Typing area */}
        <div className="lg:w-1/2 h-1/2 lg:h-full flex flex-col">
          {doc.revision_requested && doc.revision_notes && (
            <div className="bg-[var(--brand-warning)]/10 border-b border-[var(--brand-warning)]/20 p-3">
              <p className="text-xs font-bold text-[var(--brand-warning)] mb-1">Revision Requested</p>
              <p className="text-sm text-[var(--brand-text)]">{doc.revision_notes}</p>
            </div>
          )}

          {doc.ocr_text && doc.ocr_confidence > 0.3 && (
            <div className="bg-[var(--brand-primary)]/5 border-b border-[var(--brand-primary)]/10 p-3">
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-[var(--brand-primary)] mb-1">
                AI Pre-fill ({Math.round(doc.ocr_confidence * 100)}% confidence)
              </p>
            </div>
          )}

          <textarea
            data-testid="workspace-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Start typing the transcription here..."
            className="flex-1 w-full resize-none p-4 text-sm font-mono leading-relaxed focus:outline-none bg-white"
            disabled={!canType}
          />

          {/* Action bar */}
          <div className="h-14 border-t border-[var(--brand-border)] bg-white flex items-center justify-between px-4">
            <span className="text-xs text-[var(--brand-text-sec)]">
              {text.split(/\s+/).filter(Boolean).length} words
            </span>
            <div className="flex gap-2">
              <button type="button" onClick={() => navigate(`/editor/${id}`)} className="border border-[var(--brand-border)] px-3 py-2 text-sm flex items-center gap-2">
                <PanelsTopLeft size={14} /> Advanced editor
              </button>
              {canStart && (
                <button
                  data-testid="start-typing-btn"
                  onClick={handleStartTyping}
                  className="bg-[var(--brand-accent)] text-white rounded-sm px-4 py-2 text-sm font-medium hover:bg-[var(--brand-accent)]/90 flex items-center gap-2"
                >
                  <Play size={14} /> Start Typing
                </button>
              )}
              {(doc.status === 'typing') && (
                <button
                  data-testid="submit-work-btn"
                  onClick={handleSubmit}
                  disabled={submitting || !text.trim()}
                  className="bg-[var(--brand-primary)] text-white rounded-sm px-4 py-2 text-sm font-medium hover:bg-[var(--brand-primary-hover)] flex items-center gap-2 disabled:opacity-50"
                >
                  {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  Submit Work
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
